import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCampaigns, getGoalMap, getMyGoals, setCampaignGoal } from '../utils/api';
import { CAMPAIGNS } from '../constants/testIds';
import { Search, Filter } from 'lucide-react';
import { tradeColor } from '../utils/tradeColors';
import Layout from '../components/Layout';
import { CardGoalPicker } from '../components/CardGoalPicker';

const Campaigns = () => {
  const { trade } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRevenue, setSelectedRevenue] = useState('');
  const [goalMap, setGoalMap] = useState(null);
  const [myGoals, setMyGoals] = useState({});

  useEffect(() => {
    loadCampaigns();
  }, [trade]);

  useEffect(() => {
    Promise.all([getGoalMap(), getMyGoals()])
      .then(([m, g]) => { setGoalMap(m.data); setMyGoals(g.data); })
      .catch((e) => console.error('goals load failed', e));
  }, []);

  const handleGoalSelect = async (campaignId, goalId) => {
    if (!goalId) return;
    setMyGoals((p) => ({ ...p, [campaignId]: goalId }));
    try { await setCampaignGoal(campaignId, goalId); } catch (e) { console.error('goal save failed', e); }
  };

  const loadCampaigns = async () => {
    try {
      const params = {};
      if (trade) params.trade = trade.toUpperCase();
      
      const { data } = await getCampaigns(params);
      setCampaigns(data);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      !searchTerm ||
      campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.problem.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRevenue =
      !selectedRevenue || campaign.revenue_badge === selectedRevenue;

    return matchesSearch && matchesRevenue;
  });

  const getRevenueBadgeClass = (badge) => {
    if (badge?.includes('Major')) return 'badge-major';
    if (badge?.includes('High')) return 'badge-high';
    if (badge?.includes('Core')) return 'badge-core';
    if (badge?.includes('Growth')) return 'badge-growth';
    if (badge?.includes('Quick')) return 'badge-quick';
    return 'badge';
  };

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout variant="light">
      <div data-testid={CAMPAIGNS.listContainer} className="fade-in pt-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {trade ? `${trade.toUpperCase()} Campaigns` : 'All Campaigns'}
          </h1>
          <p className="text-light-gray">
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''} available
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted" size={20} />
            <input
              data-testid={CAMPAIGNS.searchInput}
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-12"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted" size={20} />
            <select
              data-testid={CAMPAIGNS.filterTrade}
              value={selectedRevenue}
              onChange={(e) => setSelectedRevenue(e.target.value)}
              className="input-field pl-12 pr-8 w-full md:w-64"
            >
              <option value="">All Revenue Levels</option>
              <option value="🔴 Major">🔴 Major</option>
              <option value="🟠 High">🟠 High</option>
              <option value="🔵 Core">🔵 Core</option>
              <option value="🟣 Growth">🟣 Growth</option>
              <option value="🟢 Quick Cash">🟢 Quick Cash</option>
            </select>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaign/${campaign.id}`}
              data-testid={CAMPAIGNS.campaignCard}
              className="card hover:scale-105 transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {campaign.campaign_name || campaign.title}
                  </h3>
                  <p className={`text-sm font-medium mb-1 ${tradeColor(campaign.trade).text}`}>
                    {campaign.trade}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div>
                  <p className="text-xs text-muted">Problem</p>
                  <p className="text-sm text-light-gray">{campaign.problem}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">Primary Service</p>
                  <p className="text-sm text-light-gray">{campaign.primary_service}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-divider">
                <span className={`badge ${getRevenueBadgeClass(campaign.revenue_badge)}`}>
                  {campaign.revenue_badge}
                </span>
              </div>

              {campaign.seasonality && (
                <div className="mt-2">
                  <span className="text-xs text-muted">
                    {campaign.seasonality}
                  </span>
                </div>
              )}

              <CardGoalPicker
                campaignId={campaign.id}
                goalMap={goalMap}
                value={myGoals[campaign.id]}
                onSelect={handleGoalSelect}
              />
            </Link>
          ))}
        </div>

        {filteredCampaigns.length === 0 && (
          <div className="card text-center py-12">
            <p className="text-light-gray">No campaigns found matching your criteria.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Campaigns;
